/**
 * The chat SESSION owns its MCP tool configuration.
 *
 * `POST /sessions/{id}/messages` merges the client's `message_context` OVER the
 * persisted `chat.context` (`shared/chat/turn_runner.py`), so any tool-scoping
 * key an embedder puts in a turn's context silently wins over what the user
 * picked — for that turn and every turn after. Decision A
 * (`docs/modules/ai-chat/ai-chat-module-design.md` § "Whose MCP config wins"):
 * a turn carries page/entity context only, and an embedder that wants to
 * re-scope a bound session PATCHes the session instead.
 *
 * These helpers are the single statement of both halves.
 */
import type { McpConfig } from './types';

/**
 * The session-context keys the tool-config picker owns. A turn must not carry
 * any of them; the persisted session is the source of truth.
 */
export const MCP_SESSION_CONFIG_CONTEXT_KEYS: ReadonlySet<string> = new Set([
  'enabled_modules',
  'module_access',
  'tool_scopes',
  'enabled_tools',
  'disabled_tools',
  'web_search',
  'web_fetch',
  'web_navigate',
]);

/** Access levels as the session context and the update payload spell them. */
export type ModuleAccessMap = Record<string, 'read' | 'readwrite'>;

/**
 * Project an McpConfig onto the snake_case chat-session `context` keys the
 * backend reads. Omits absent keys so a spread never blanks out a value the
 * caller's base context already carries. Used at session CREATE, where the
 * picker's pre-session choice is folded into the persisted context.
 */
export function mcpConfigToContext(config: McpConfig | null): Record<string, unknown> {
  if (!config) return {};
  return {
    enabled_modules: config.enabledModules,
    ...(config.moduleAccess ? { module_access: config.moduleAccess } : {}),
    ...(config.enabledTools ? { enabled_tools: config.enabledTools } : {}),
    ...(config.disabledTools ? { disabled_tools: config.disabledTools } : {}),
    ...(config.webSearchEnabled !== undefined ? { web_search: config.webSearchEnabled } : {}),
    ...(config.webFetchEnabled !== undefined ? { web_fetch: config.webFetchEnabled } : {}),
    ...(config.webNavigateEnabled !== undefined ? { web_navigate: config.webNavigateEnabled } : {}),
  };
}

/**
 * The context a TURN may carry: the embedder's live page/entity keys, with
 * everything the session owns removed. Returns `undefined` when nothing is
 * left, so the caller sends no `message_context` at all.
 */
export function toTurnMessageContext(
  context: Record<string, unknown> | undefined | null,
): Record<string, unknown> | undefined {
  if (!context) return undefined;
  const turnContext: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(context)) {
    if (MCP_SESSION_CONFIG_CONTEXT_KEYS.has(key)) continue;
    turnContext[key] = value;
  }
  return Object.keys(turnContext).length > 0 ? turnContext : undefined;
}

/** What the session currently holds, and what the embedder's page scope needs. */
export interface ScopeSyncInput {
  /** The session's persisted `enabled_modules`. */
  persistedModules: string[];
  /** The session's persisted `context`. */
  persistedContext: Record<string, unknown> | undefined;
  /** The embedder's current page scope. */
  embedderModules: string[];
  embedderAccess: ModuleAccessMap | undefined;
  embedderToolScopes?: Record<string, string[]> | undefined;
}

/** The subset of the update payload a scope sync writes. */
export interface ScopeSyncPatch {
  enabled_modules?: string[];
  module_access?: ModuleAccessMap;
  tool_scopes?: Record<string, string[]>;
}

/**
 * The union rule: an embedder re-scoping a bound session may ADD the current
 * page's modules and may never remove — or re-level — a module the session
 * already carries. That preserves "Luma follows the page" for the global panel
 * while leaving the user's own picker choices (an added module, a downgraded
 * access level) untouched.
 *
 * Returns `null` when the session already covers the page, so the caller sends
 * no request. Pure, so the decision is testable without a rendered panel.
 */
export function resolveScopeSyncPatch({
  persistedModules,
  persistedContext,
  embedderModules,
  embedderAccess,
  embedderToolScopes,
}: ScopeSyncInput): ScopeSyncPatch | null {
  const patch: ScopeSyncPatch = {};

  const missing = embedderModules.filter((module) => !persistedModules.includes(module));
  if (missing.length > 0) {
    const persistedAccess = (persistedContext?.module_access ?? {}) as ModuleAccessMap;
    const addedAccess: ModuleAccessMap = {};
    for (const module of missing) {
      const level = embedderAccess?.[module];
      if (level) addedAccess[module] = level;
    }
    patch.enabled_modules = [...persistedModules, ...missing];
    patch.module_access = { ...persistedAccess, ...addedAccess };
  }

  // Tool scopes merge PER MODULE, for the same reason module_access does — and
  // here the stake is higher: a module listed in `enabled_modules` with NO entry
  // in `tool_scopes` gets no scope filtering at all (`_get_allowed_ops_for_module`
  // returns None → every operation allowed). So replacing the map wholesale with
  // the current page's would silently WIDEN a module scoped by an earlier
  // embedder, on a session the union rule above deliberately never narrows.
  // The embedder therefore adds scopes for modules the session has none for, and
  // never rewrites one it already carries.
  if (embedderToolScopes) {
    const persistedScopes = (persistedContext?.tool_scopes ?? {}) as Record<string, string[]>;
    const addedScopes: Record<string, string[]> = {};
    for (const [module, scopes] of Object.entries(embedderToolScopes)) {
      if (!(module in persistedScopes)) addedScopes[module] = scopes;
    }
    if (Object.keys(addedScopes).length > 0) {
      patch.tool_scopes = { ...persistedScopes, ...addedScopes };
    }
  }

  return Object.keys(patch).length > 0 ? patch : null;
}
